'use client';

import { useState, useRef, useEffect } from 'react';
import Image from 'next/image';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function PharmaLoginModal({ isOpen, onClose, onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setError('');
      setLoading(false);
      setTimeout(() => emailRef.current?.focus(), 80);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    setTimeout(() => {
      if (email.trim().toLowerCase() === 'admin@gmail.com' && password === 'admin123') {
        setLoading(false);
        onSuccess();
      } else {
        setLoading(false);
        setError('Invalid credentials. Please try again.');
      }
    }, 600);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(10, 16, 33, 0.82)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(145deg, #1e2f50 0%, #1B2845 60%, #162038 100%)',
          border: '1px solid rgba(197,165,114,0.18)',
          boxShadow: '0 32px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(197,165,114,0.08)',
        }}
      >
        {/* Top gold accent line */}
        <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, #C5A572 40%, #C5A572 60%, transparent)' }} />

        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-200"
          style={{ color: 'rgba(197,165,114,0.5)' }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#C5A572')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(197,165,114,0.5)')}
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="px-8 pt-8 pb-9">
          {/* Logo / Brand mark */}
          <div className="flex flex-col items-center mb-7">
            <Image
              src="/assets/images/All-SIMAX-LOGOS_-_9_-_Edited-1770650153046.png"
              alt="Simax Systems Logo"
              width={120}
              height={120}
              className="object-contain mb-2"
              priority
            />
            <h2 className="font-serif text-white text-2xl font-bold tracking-tight">Pharma Intelligence</h2>
            <p className="text-sm mt-1" style={{ color: 'rgba(248,246,243,0.5)' }}>Simax Systems · Secure Access</p>
          </div>

          {/* Divider */}
          <div className="mb-6" style={{ height: '1px', background: 'rgba(197,165,114,0.12)' }} />

          <form onSubmit={handleSubmit} noValidate>
            {/* Email */}
            <div className="mb-4">
              <label className="block text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: 'rgba(197,165,114,0.8)' }}>
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(197,165,114,0.45)' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M2 4h12v8H2V4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 4l6 5 6-5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                <input
                  ref={emailRef}
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="Enter your email"
                  autoComplete="username"
                  required
                  className="w-full pl-10 pr-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${error ? 'rgba(239,68,68,0.5)' : 'rgba(197,165,114,0.2)'}`,
                    color: '#F8F6F3',
                    caretColor: '#C5A572',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(197,165,114,0.6)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onBlur={(e) => { e.currentTarget.style.border = `1px solid ${error ? 'rgba(239,68,68,0.5)' : 'rgba(197,165,114,0.2)'}`; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                />
              </div>
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="block text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: 'rgba(197,165,114,0.8)' }}>
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(197,165,114,0.45)' }}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <rect x="3" y="7" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.2"/>
                    <path d="M5 7V5a3 3 0 016 0v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  className="w-full pl-10 pr-10 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${error ? 'rgba(239,68,68,0.5)' : 'rgba(197,165,114,0.2)'}`,
                    color: '#F8F6F3',
                    caretColor: '#C5A572',
                  }}
                  onFocus={(e) => { e.currentTarget.style.border = '1px solid rgba(197,165,114,0.6)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                  onBlur={(e) => { e.currentTarget.style.border = `1px solid ${error ? 'rgba(239,68,68,0.5)' : 'rgba(197,165,114,0.2)'}`; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors duration-150"
                  style={{ color: 'rgba(197,165,114,0.4)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#C5A572')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(197,165,114,0.4)')}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      <circle cx="8" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                      <path d="M3 3l10 10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M2 8s2.5-4 6-4 6 4 6 4-2.5 4-6 4-6-4-6-4z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                      <circle cx="8" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.2"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div
                className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg mb-4 text-sm"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', color: '#fca5a5' }}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0">
                  <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.2"/>
                  <path d="M7 4v3.5M7 9.5v.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                {error}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-lg font-semibold text-sm tracking-wide transition-all duration-200 flex items-center justify-center gap-2"
              style={{
                background: loading ? 'rgba(197,165,114,0.5)' : '#C5A572',
                color: '#1B2845',
                boxShadow: loading ? 'none' : '0 4px 20px rgba(197,165,114,0.3)',
              }}
              onMouseEnter={(e) => { if (!loading) (e.currentTarget as HTMLElement).style.background = '#d4b483'; }}
              onMouseLeave={(e) => { if (!loading) (e.currentTarget as HTMLElement).style.background = '#C5A572'; }}
            >
              {loading ? (
                <>
                  <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                    <path d="M8 2a6 6 0 016 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  Verifying…
                </>
              ) : (
                <>
                  Access Dashboard
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Footer note */}
          <p className="text-center mt-5 text-xs" style={{ color: 'rgba(248,246,243,0.25)' }}>
            Restricted access · Simax Systems Pharma Intelligence Suite
          </p>
        </div>
      </div>
    </div>
  );
}
